import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldAlert, AlertTriangle, XCircle, FileText, Scale } from 'lucide-react';

const RefundPolicy = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ background: '#0f0a1e', minHeight: '100vh', paddingTop: '100px', paddingBottom: '80px' }}>
      <div style={{ maxWidth: '820px', margin: '0 auto', padding: '0 24px' }}>

        {/* Back button */}
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'none',
            border: 'none',
            color: '#00d4ff',
            fontSize: 14,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginBottom: 32,
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
          }}
        >
          <ArrowLeft size={18} /> Back to Home
        </button>

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.3)',
            borderRadius: 8,
            padding: '6px 16px',
            marginBottom: 20,
          }}>
            <ShieldAlert size={16} color="#ef4444" />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#ef4444', letterSpacing: 1, textTransform: 'uppercase' }}>
              Strict No-Refund Policy
            </span>
          </div>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: 'clamp(32px, 4vw, 48px)',
            fontWeight: 800,
            color: '#ffffff',
            marginBottom: 12,
            lineHeight: 1.2,
          }}>
            Refund Policy
          </h1>
          <p style={{ fontSize: 14, color: '#8885a0' }}>
            Last updated: March 29, 2026 &nbsp;|&nbsp; Effective immediately upon purchase
          </p>
        </div>

        {/* Critical Notice */}
        <div style={{
          background: 'rgba(239,68,68,0.08)',
          border: '1px solid rgba(239,68,68,0.3)',
          borderRadius: 14,
          padding: '28px 32px',
          marginBottom: 40,
          display: 'flex',
          gap: 16,
        }}>
          <AlertTriangle size={24} color="#ef4444" style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#ef4444', marginBottom: 8 }}>
              IMPORTANT — PLEASE READ BEFORE PURCHASING
            </h3>
            <p style={{ fontSize: 15, color: '#e0dff0', lineHeight: 1.8 }}>
              All sales of digital products and services offered by The Inner Bridge (Innovation Lifestyle Consultancy L.L.C-FZ) are <strong style={{ color: '#ffffff' }}>final and non-refundable</strong>. By completing your purchase, you acknowledge and agree that <strong style={{ color: '#ffffff' }}>no refunds, credits, exchanges, or chargebacks</strong> will be issued under any circumstances.
            </p>
          </div>
        </div>

        {/* Sections */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 36 }}>

          {/* Section 1 */}
          <div className="glass-card" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <XCircle size={18} color="#ef4444" />
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#ffffff' }}>1. All Sales Are Final</h2>
            </div>
            <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8, marginBottom: 16 }}>
              Due to the <strong style={{ color: '#e0dff0' }}>digital nature</strong> of all products and services offered on this platform — including but not limited to online courses, digital books, e-learning materials, downloadable content, membership access, and application subscriptions — <strong style={{ color: '#e0dff0' }}>all sales are final and absolutely non-refundable</strong>.
            </p>
            <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8 }}>
              Once a digital product has been purchased and access has been granted, the transaction is considered complete and irreversible. Digital goods cannot be "returned" as they are instantly delivered and consumed upon access.
            </p>
          </div>

          {/* Section 2 */}
          <div className="glass-card" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(239,68,68,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <XCircle size={18} color="#ef4444" />
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#ffffff' }}>2. No Exceptions Policy</h2>
            </div>
            <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8, marginBottom: 16 }}>
              Refunds will <strong style={{ color: '#ef4444' }}>NOT</strong> be granted for any of the following reasons:
            </p>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'Change of mind or buyer\'s remorse after purchase',
                'Failure to access or complete the course/content within the available timeframe',
                'Dissatisfaction with the content, teaching style, or methodology',
                'Duplicate purchases or accidental purchases',
                'Technical issues on the buyer\'s end (internet, device compatibility, etc.)',
                'Inability to attend live sessions or access materials on time',
                'Claims that the content did not meet personal expectations',
                'Partial completion or non-completion of any course or program',
                'Account suspension due to violation of our Terms of Service',
                'Requesting a refund through your bank, payment processor, or credit card company (chargebacks)',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <XCircle size={16} color="#ef4444" style={{ flexShrink: 0, marginTop: 3 }} />
                  <span style={{ fontSize: 14, color: '#b0adc0', lineHeight: 1.6 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 3 */}
          <div className="glass-card" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(245,158,11,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AlertTriangle size={18} color="#f59e0b" />
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#ffffff' }}>3. Chargeback Warning</h2>
            </div>
            <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8, marginBottom: 16 }}>
              Filing a chargeback or payment dispute with your bank or payment provider constitutes a <strong style={{ color: '#ef4444' }}>violation of this agreement</strong> and will result in:
            </p>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'Immediate and permanent termination of your account and all access to purchased content',
                'Blacklisting from all future purchases on Inner Bridge and affiliated platforms',
                'Collection proceedings for the disputed amount plus all associated fees',
                'Reporting to fraud prevention databases and credit agencies where applicable',
                'Legal action to recover all costs, including attorney fees and administrative charges',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <AlertTriangle size={14} color="#f59e0b" style={{ flexShrink: 0, marginTop: 4 }} />
                  <span style={{ fontSize: 14, color: '#b0adc0', lineHeight: 1.6 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 4 */}
          <div className="glass-card" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} color="#8b5cf6" />
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#ffffff' }}>4. Acknowledgement at Purchase</h2>
            </div>
            <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8 }}>
              By completing any purchase on this platform, you <strong style={{ color: '#e0dff0' }}>explicitly acknowledge and agree</strong> to the following:
            </p>
            <ul style={{ listStyle: 'none', padding: 0, marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'You have read, understood, and accepted this No-Refund Policy in its entirety before making your purchase.',
                'You understand that all digital products are delivered instantly and cannot be returned or undelivered.',
                'You waive any and all rights to request a refund, chargeback, or reversal of the transaction.',
                'You accept full responsibility for your purchase decision.',
                'You acknowledge that this policy is legally binding and enforceable under the laws of the United Arab Emirates.',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <FileText size={14} color="#8b5cf6" style={{ flexShrink: 0, marginTop: 4 }} />
                  <span style={{ fontSize: 14, color: '#b0adc0', lineHeight: 1.6 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 5 */}
          <div className="glass-card" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} color="#8b5cf6" />
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#ffffff' }}>5. Digital Product Delivery</h2>
            </div>
            <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8, marginBottom: 16 }}>
              All digital products are delivered electronically and access is provided immediately upon successful payment processing. Once access is granted:
            </p>
            <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[
                'The product is considered fully delivered regardless of whether the buyer accesses or uses it.',
                'Course enrollment is immediate and irrevocable.',
                'Download links, streaming access, and membership privileges are activated instantly.',
                'The buyer bears sole responsibility for consuming the content within any specified timeframes.',
              ].map((item, i) => (
                <li key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#8b5cf6', flexShrink: 0, marginTop: 8 }} />
                  <span style={{ fontSize: 14, color: '#b0adc0', lineHeight: 1.6 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 6 */}
          <div className="glass-card" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Scale size={18} color="#8b5cf6" />
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#ffffff' }}>6. Governing Law & Jurisdiction</h2>
            </div>
            <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8 }}>
              This Refund Policy is governed by and construed in accordance with the laws of the <strong style={{ color: '#e0dff0' }}>United Arab Emirates</strong>. Any disputes arising from or related to this policy shall be subject to the exclusive jurisdiction of the courts of <strong style={{ color: '#e0dff0' }}>Dubai, U.A.E.</strong> By purchasing from The Inner Bridge, you consent to the personal jurisdiction of said courts.
            </p>
          </div>

          {/* Section 7 */}
          <div className="glass-card" style={{ padding: '32px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(139,92,246,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} color="#8b5cf6" />
              </div>
              <h2 style={{ fontSize: 20, fontWeight: 700, color: '#ffffff' }}>7. Contact Information</h2>
            </div>
            <p style={{ fontSize: 15, color: '#b0adc0', lineHeight: 1.8, marginBottom: 16 }}>
              If you have questions about this policy <strong style={{ color: '#e0dff0' }}>before making a purchase</strong>, you may contact us:
            </p>
            <div style={{ background: 'rgba(88,28,135,0.15)', borderRadius: 10, padding: '16px 20px' }}>
              <p style={{ fontSize: 14, color: '#e0dff0', fontWeight: 600, marginBottom: 4 }}>The Inner Bridge — Innovation Lifestyle Consultancy L.L.C-FZ</p>
              <p style={{ fontSize: 13, color: '#8885a0' }}>Meydan Grandstand, 6th Floor, Meydan Road, Nad Al Sheba, Dubai, U.A.E.</p>
              <p style={{ fontSize: 13, color: '#8885a0' }}>License Number: 2646553.01</p>
            </div>
          </div>

        </div>

        {/* Final Warning */}
        <div style={{
          marginTop: 48,
          background: 'rgba(239,68,68,0.06)',
          border: '1px solid rgba(239,68,68,0.25)',
          borderRadius: 14,
          padding: '24px 32px',
          textAlign: 'center',
        }}>
          <p style={{ fontSize: 14, color: '#ef4444', fontWeight: 700, marginBottom: 8, letterSpacing: 0.5 }}>
            BY MAKING A PURCHASE, YOU CONFIRM THAT YOU HAVE READ, UNDERSTOOD, AND IRREVOCABLY AGREE TO THIS STRICT NO-REFUND POLICY.
          </p>
          <p style={{ fontSize: 13, color: '#8885a0' }}>
            This policy applies to all products and services, past, present, and future, offered by The Inner Bridge.
          </p>
        </div>

      </div>
    </div>
  );
};

export default RefundPolicy;
